'use client'

import { convertCurrency, DEFAULT_CURRENCY, formatCurrencyPrice } from '@/lib/services/currency/conversion'
import { Currency } from '@prisma/client'
import { useCallback } from 'react'
import { usePreferences } from './use-preferences'

/**
 * Hook for currency operations with user preferences
 *
 * Provides:
 * - Currency conversion from CAD (system default) to user's preferred currency
 * - Formatted currency display with proper symbols and locale
 * - Access to user's currency preference
 * - Exchange rate information
 */
export function useCurrency() {
  const { preferences } = usePreferences()
  const userCurrency = (preferences.ui.currency as Currency) || DEFAULT_CURRENCY

  /**
   * Convert price from CAD to user's preferred currency
   * @param priceCAD Price in Canadian dollars (system default)
   * @returns Price in user's preferred currency
   */
  const convertFromCAD = useCallback((priceCAD: number): number => {
    return convertCurrency(priceCAD, DEFAULT_CURRENCY, userCurrency)
  }, [userCurrency])

  /**
   * Convert price from user's currency to CAD
   * @param price Price in user's currency
   * @returns Price in Canadian dollars
   */
  const convertToCAD = useCallback((price: number): number => {
    return convertCurrency(price, userCurrency, DEFAULT_CURRENCY)
  }, [userCurrency])

  /**
   * Format price in user's preferred currency
   * @param priceCAD Price in Canadian dollars (system default)
   * @param locale Locale for formatting (default: en-CA)
   * @returns Formatted currency string
   */
  const format = useCallback((priceCAD: number, locale: string = 'en-CA'): string => {
    const convertedPrice = convertFromCAD(priceCAD)
    return formatCurrencyPrice(convertedPrice, userCurrency, locale)
  }, [convertFromCAD, userCurrency])

  /**
   * Format price that's already in user's currency
   * @param price Price already in user's currency
   * @param locale Locale for formatting (default: en-CA)
   * @returns Formatted currency string
   */
  const formatConverted = useCallback((price: number, locale: string = 'en-CA'): string => {
    return formatCurrencyPrice(price, userCurrency, locale)
  }, [userCurrency])

  /**
   * Get exchange rate from CAD to user's currency
   * @returns Exchange rate (1.0 if user currency is CAD)
   */
  const getExchangeRate = useCallback((): number => {
    if (userCurrency === DEFAULT_CURRENCY) {
      return 1.0
    }
    return convertFromCAD(1.0)
  }, [convertFromCAD, userCurrency])

  return {
    userCurrency,
    isDefaultCurrency: userCurrency === DEFAULT_CURRENCY,
    convertFromCAD,
    convertToCAD,
    format,
    formatConverted,
    getExchangeRate
  }
}
